// src/Auth/ForgotPassword.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { NativeAuthClient } from "../Services/Auth/NativeAuthClient";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { AuthContext } from "./AuthProvider";

type Phase = "enterEmail" | "awaitCode" | "setPassword" | "polling" | "done" | "error";

const client = new NativeAuthClient();

const ForgotPassword: React.FC = () => {
    const auth = React.useContext(AuthContext); // <-- add AuthContext
    const [params] = useSearchParams();
    const navigate = useNavigate();
    //const location = useLocation();

    const initialEmail = useMemo(() => params.get("email") ?? "", [params]);
    const [email, setEmail] = useState(initialEmail);
    const [otp, setOtp] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [phase, setPhase] = useState<Phase>("enterEmail");
    const [error, setError] = useState<string>("");
    const [busy, setBusy] = useState(false);

    const pollTimerRef = useRef<number | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
        };
    }, []);

    const doneRedirect = () => {
        const target = localStorage.getItem("redirectUri") || "/";
        try { localStorage.removeItem("redirectUri"); } catch { }
        navigate(target, { replace: true });
    };

    const start = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mountedRef.current) return;
        setError("");
        setBusy(true);

        if (!email) {
            setError("Email is required.");
            setBusy(false);
            return;
        }

        try {
            await client.ssprStart(email);      // POST /api/nativeauth/reset/start
            if (!mountedRef.current) return;
            await client.ssprChallenge();       // POST /api/nativeauth/reset/challenge
            if (!mountedRef.current) return;
            setPhase("awaitCode");
        } catch (err: any) {
            if (!mountedRef.current) return;
            setError(err?.message ?? "Failed to start password reset.");
            setPhase("error");
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const verifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mountedRef.current) return;
        setError("");
        setBusy(true);

        if (!otp) {
            setError("Enter the code sent to your email.");
            setBusy(false);
            return;
        }

        try {
            await client.ssprContinueOtp(otp);  // POST /api/nativeauth/reset/continue
            if (!mountedRef.current) return;
            setPhase("setPassword");
        } catch (err: any) {
            if (!mountedRef.current) return;
            setError(err?.message ?? "Invalid/expired code. Try resending.");
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const submitNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mountedRef.current) return;
        setError("");
        setBusy(true);

        if (!password) {
            setError("New password is required.");
            setBusy(false);
            return;
        }
        if (password !== confirm) {
            setError("Passwords don’t match.");
            setBusy(false);
            return;
        }

        try {
            const { poll_interval } = await client.ssprSubmitNewPassword(password); // /reset/submit
            if (!mountedRef.current) return;
            setPhase("polling");

            const poll = async () => {
                try {
                    const { status, continuation_token } = await client.ssprPollCompletion(); // /reset/poll
                    if (!mountedRef.current) return;

                    if (status === "succeeded") {
                        // ✅ Sign the user in right here
                        const tokens = await client.tokenWithContinuation(continuation_token!, email);
                        if (!mountedRef.current) return;

                        // Save tokens in your auth layer so the gate sees you as authenticated
                        await auth.setNativeSession({ ...tokens, username: email });

                        setPhase("done");
                        doneRedirect(); // go to original target (or '/')
                    } else if (status === "failed") {
                        setError("Password reset failed. Please try again.");
                        setPhase("error");
                    } else {
                        // not_started / in_progress — keep polling
                        pollTimerRef.current = window.setTimeout(
                            poll,
                            Math.max(2, poll_interval ?? 2) * 1000
                        ) as unknown as number;
                    }
                } catch (e: any) {
                    if (!mountedRef.current) return;
                    setError(e?.message ?? "Password reset status check failed.");
                    setPhase("error");
                }
            };

            poll();
        } catch (err: any) {
            if (!mountedRef.current) return;
            setError(err?.message ?? "Submitting new password failed.");
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const resendCode = async () => {
        if (!mountedRef.current) return;
        setError("");
        setBusy(true);
        try {
            await client.ssprChallenge(); // re-send OTP
        } catch (e: any) {
            if (!mountedRef.current) return;
            setError(e?.message ?? "Could not resend code.");
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col xs={12} sm={10} md={7} lg={5}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title as="h2" className="h4 mb-3">
                                Change password:
                            </Card.Title>

                            {phase === "enterEmail" && (
                                <Form onSubmit={start} noValidate>
                                    <Form.Group className="mb-3" controlId="fp-email">
                                        <Form.Label>Email address</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            autoFocus
                                            disabled={busy}
                                            placeholder="you@example.com"
                                        />
                                    </Form.Group>
                                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                                    <div className="d-grid">
                                        <Button type="submit" disabled={!email || busy}>
                                            {busy ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Sending…</>) : "Send code"}
                                        </Button>
                                    </div>
                                </Form>
                            )}

                            {phase === "awaitCode" && (
                                <Form onSubmit={verifyCode} noValidate>
                                    <p className="mb-2">We sent a one-time code to <b>{email}</b>.</p>
                                    <Form.Group className="mb-3" controlId="fp-otp">
                                        <Form.Label>Enter code</Form.Label>
                                        <Form.Control
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            inputMode="numeric"
                                            required
                                            disabled={busy}
                                            placeholder="Enter the code"
                                        />
                                    </Form.Group>
                                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                                    <div className="d-flex gap-2">
                                        <Button type="submit" disabled={!otp || busy}>
                                            {busy ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Verifying…</>) : "Verify code"}
                                        </Button>
                                        <Button variant="link" type="button" onClick={resendCode} disabled={busy}>
                                            Resend code
                                        </Button>
                                    </div>
                                </Form>
                            )}

                            {phase === "setPassword" && (
                                <Form onSubmit={submitNewPassword} noValidate>
                                    <Form.Group className="mb-3" controlId="fp-pass">
                                        <Form.Label>New password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            disabled={busy}
                                            placeholder="Enter new password"
                                            autoComplete="new-password"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="fp-confirm">
                                        <Form.Label>Confirm password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            required
                                            disabled={busy}
                                            placeholder="Re-enter new password"
                                            autoComplete="new-password"
                                        />
                                    </Form.Group>
                                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                                    <div className="d-grid">
                                        <Button type="submit" disabled={!password || !confirm || busy}>
                                            {busy ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>) : "Save new password"}
                                        </Button>
                                    </div>
                                </Form>
                            )}

                            {phase === "polling" && (
                                <div className="text-center">
                                    <AbpnSpinner />
                                    <p className="mt-2 mb-0">Finalizing your password…</p>
                                </div>
                            )}

                            {phase === "done" && (
                                <Alert variant="success" className="mb-0">
                                    Password updated! Redirecting…
                                </Alert>
                            )}

                            {phase === "error" && !error && (
                                <Alert variant="danger" className="mt-3 mb-0">
                                    Something went wrong. Please try again.
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ForgotPassword;
